import { ApiReturnMfe } from '@highimpact-frontend/api-types';
import { keepLatestMfeVersions } from '../../internal/mfe/keepLatestMfeVersions';
import { useDashboardHost } from '../../context/DashboardHostContext';
import { useEffect, useState } from 'react';

// -- type definitions --

export interface widgetCreationInfo {
  label: string;
  formTitle: string;
  specificWidgetType?: string;
  pluginName?: string;
}

export interface WidgetCreationData {
  widgets: Record<string, widgetCreationInfo>;
  mfes: ApiReturnMfe[];
}

interface widgetListResponseLoaded {
  data: WidgetCreationData;
  state: 'loaded';
}

interface widgetListResponsePending {
  state: 'pending';
}

type widgetListResponse = widgetListResponseLoaded | widgetListResponsePending;

// -- constants --

/**
 * List of defined widgts to start with.
 */
const ELEMENTTYPEPRETTYNAME: Record<string, widgetCreationInfo> = {
  plot: { label: 'Graphique', formTitle: 'Ajouter un graphique' },
  table: {
    label: 'Tableau de données',
    formTitle: 'Ajouter un tableau de données',
  },
  tablebycampaign: {
    label: 'Tableau de campagnes',
    formTitle: 'Ajouter un tableau de campagnes',
  },
  text: { label: 'Zone de texte', formTitle: 'Ajouter une zone de texte' },
  localizationfromcoordinates: {
    label: "Coordonnées d'une ressource",
    formTitle: "Ajouter des coordonnées d'une ressource",
  },
  image: { label: 'Image', formTitle: 'Ajouter une image' },
  imagegallery: {
    label: "Galerie d'images",
    formTitle: "Ajouter une galerie d'images",
  },
};

// -- helper functions --

const createWidgetInfo = (
  mfes: ApiReturnMfe[]
): Record<string, widgetCreationInfo> => {
  return mfes.reduce((acc, mfe) => {
    return {
      ...acc,
      [mfe.name]: {
        label: mfe.pretty_name,
        formTitle: `Ajouter un élement "${mfe.pretty_name}"`,
        specificWidgetType: mfe.name,
        pluginName: mfe.plugin_name,
      },
    };
  }, ELEMENTTYPEPRETTYNAME);
};

// -- main hook --

export const useWidgetCreationInfo = (): widgetListResponse => {
  const host = useDashboardHost();
  const [mfeData, setMfeData] = useState<widgetListResponse>({
    state: 'pending',
  });

  useEffect(() => {
    if (sessionStorage.getItem('DO_NOT_RELOAD_WIDGET_DATA') === 'true') {
      setMfeData({
        state: 'loaded',
        data: { widgets: ELEMENTTYPEPRETTYNAME, mfes: [] },
      });
      return;
    }
    host.api.mfe
      .getMfes()
      .then((mfes) => {
        const latestMfes = keepLatestMfeVersions(mfes);
        setMfeData({
          state: 'loaded',
          data: { widgets: createWidgetInfo(latestMfes), mfes: latestMfes },
        });
      })
      .catch((error: unknown) => {
        console.error(error instanceof Error ? error.message : String(error));
        setMfeData({
          state: 'loaded',
          data: { widgets: ELEMENTTYPEPRETTYNAME, mfes: [] },
        });
      });
  }, [host.api.mfe]);

  return mfeData;
};
